/* eslint-disable @typescript-eslint/no-explicit-any */
import axios from 'axios';
import { call, put, takeEvery } from 'redux-saga/effects';
import { api } from 'services/api';
import { notificationContainer } from 'utils/notificationContainer';
import fileDownload from 'js-file-download';
import { PayloadAction } from '@reduxjs/toolkit';

import {
	getGeneralAttachmentsRequest,
	getGeneralAttachmentsSuccess,
	createGeneralAttachmentRequest,
	createGeneralAttachmentSuccess,
	updateGeneralAttachmentRequest,
	updateGeneralAttachmentSuccess,
	downloadGeneralAttachmentRequest,
	downloadGeneralAttachmentSuccess,
	deleteGeneralAttachmentRequest,
	deleteGeneralAttachmentSuccess,
	addGeneralAttachmentVersionRequest,
	addGeneralAttachmentVersionSuccess,
} from './reducer';
import {
	TGeneralAttachmentsListRequest,
	TGeneralAttachmentsListResponsePayload,
	TGeneralAttachmentCreateRequest,
	TGeneralAttachmentUpdateRequest,
	TGeneralAttachmentDownloadRequest,
	TGeneralAttachmentDeleteRequest,
	TGeneralAttachmentAddVersionRequest,
} from './types';

function* getGeneralAttachmentsRequestWorker(
	action: PayloadAction<TGeneralAttachmentsListRequest>,
): Generator<any, void, any> {
	const { payload } = action;
	try {
		const response: TGeneralAttachmentsListResponsePayload = yield call(
			api.generalAttachments.getList,
			payload,
		);
		yield put(getGeneralAttachmentsSuccess(response));
	} catch (error) {
		if (axios.isAxiosError(error)) {
			notificationContainer(String(error?.response?.data?.message ?? error ?? 'Error!'), 'error');
		}
		yield put(getGeneralAttachmentsSuccess(null));
	}
}

function* createGeneralAttachmentRequestWorker(
	action: PayloadAction<TGeneralAttachmentCreateRequest>,
): Generator<any, void, any> {
	const { payload } = action;
	try {
		const formData = new FormData();
		formData.append('File', payload.file);
		formData.append('TypeId', String(payload.typeId));
		if (payload.comment) formData.append('Comment', payload.comment);

		yield call(api.generalAttachments.create, { formData });
		yield put(createGeneralAttachmentSuccess());

		if (payload.listRequest) {
			yield put(getGeneralAttachmentsRequest(payload.listRequest));
		}

		notificationContainer('Attachment created successfully', 'success');
	} catch (error) {
		if (axios.isAxiosError(error)) {
			notificationContainer(String(error?.response?.data?.message ?? error ?? 'Error!'), 'error');
		}
		yield put(createGeneralAttachmentSuccess());
	}
}

function* updateGeneralAttachmentRequestWorker(
	action: PayloadAction<TGeneralAttachmentUpdateRequest>,
): Generator<any, void, any> {
	const { payload } = action;
	try {
		const formData = new FormData();
		formData.append('FileName', payload.fileName);
		if (payload.comment) formData.append('Comment', payload.comment);

		yield call(api.generalAttachments.update, { attachmentId: payload.attachmentId, formData });
		yield put(updateGeneralAttachmentSuccess());

		if (payload.listRequest) {
			yield put(getGeneralAttachmentsRequest(payload.listRequest));
		}

		notificationContainer('Attachment updated successfully', 'success');
	} catch (error) {
		if (axios.isAxiosError(error)) {
			notificationContainer(String(error?.response?.data?.message ?? error ?? 'Error!'), 'error');
		}
		yield put(updateGeneralAttachmentSuccess());
	}
}

function* downloadGeneralAttachmentRequestWorker(
	action: PayloadAction<TGeneralAttachmentDownloadRequest>,
): Generator<any, void, any> {
	const { payload } = action;
	try {
		const blob = yield call(api.generalAttachments.getOne, payload);
		yield put(downloadGeneralAttachmentSuccess());
		if (blob) fileDownload(blob, payload.fileName);
	} catch (error) {
		if (axios.isAxiosError(error)) {
			notificationContainer(String(error?.response?.data?.message ?? error ?? 'Error!'), 'error');
		}
		yield put(downloadGeneralAttachmentSuccess());
	}
}

function* deleteGeneralAttachmentRequestWorker(
	action: PayloadAction<TGeneralAttachmentDeleteRequest>,
): Generator<any, void, any> {
	const { payload } = action;
	try {
		yield call(api.generalAttachments.delete, payload.attachmentId);
		yield put(deleteGeneralAttachmentSuccess());

		if (payload.listRequest) {
			yield put(getGeneralAttachmentsRequest(payload.listRequest));
		}

		notificationContainer('Attachment deleted successfully', 'success');
	} catch (error) {
		if (axios.isAxiosError(error)) {
			notificationContainer(String(error?.response?.data?.message ?? error ?? 'Error!'), 'error');
		}
		yield put(deleteGeneralAttachmentSuccess());
	}
}

function* addGeneralAttachmentVersionRequestWorker(
	action: PayloadAction<TGeneralAttachmentAddVersionRequest>,
): Generator<any, void, any> {
	const { payload } = action;
	try {
		const formData = new FormData();
		formData.append('File', payload.file);
		if (payload.comment) formData.append('Comment', payload.comment);

		yield call(api.generalAttachments.addVersion, { attachmentId: payload.attachmentId, formData });
		yield put(addGeneralAttachmentVersionSuccess());

		if (payload.listRequest) {
			yield put(getGeneralAttachmentsRequest(payload.listRequest));
		}

		notificationContainer('New version added successfully', 'success');
	} catch (error) {
		if (axios.isAxiosError(error)) {
			notificationContainer(String(error?.response?.data?.message ?? error ?? 'Error!'), 'error');
		}
		yield put(addGeneralAttachmentVersionSuccess());
	}
}

export function* generalAttachmentsSaga(): Generator<any, void, any> {
	yield takeEvery(getGeneralAttachmentsRequest.type, getGeneralAttachmentsRequestWorker);
	yield takeEvery(createGeneralAttachmentRequest.type, createGeneralAttachmentRequestWorker);
	yield takeEvery(updateGeneralAttachmentRequest.type, updateGeneralAttachmentRequestWorker);
	yield takeEvery(downloadGeneralAttachmentRequest.type, downloadGeneralAttachmentRequestWorker);
	yield takeEvery(deleteGeneralAttachmentRequest.type, deleteGeneralAttachmentRequestWorker);
	yield takeEvery(
		addGeneralAttachmentVersionRequest.type,
		addGeneralAttachmentVersionRequestWorker,
	);
}
